<script setup lang="ts">
import { computed, ref, nextTick } from 'vue'
import { ElMessage } from 'element-plus'
import ReportSummaryAircraftHistory from './ReportSummaryAircraftHistory.vue'
import { useFilterStore } from 'Store/filterStore'
import { formatDate } from 'Common/utils/formatDate'
import ReportSummarySeverity from './ReportSummarySeverity.vue'

import { useUpdateInspectionInstanceLabelMutation } from 'API/mutations/inspection-instance'
import { PencilIcon, CheckIcon, XMarkIcon } from 'Common/components/elements/icons/Icons'

interface PropTypes {
  aircraftData: Array<any>
  aircraftLoading: boolean
  inspectionData: Array<any>
  inspectionDataLoading: boolean
  annotationData: Array<any>
  annotationsLoading: boolean
}

const props = defineProps<PropTypes>()
const filterStore = useFilterStore()

const { mutateAsync: updateLabel, isLoading: isSavingLabel } = useUpdateInspectionInstanceLabelMutation()
const isEditingLabel = ref(false)
const newLabel = ref('')
const labelInputRef = ref()

const startEditingLabel = () => {
  if (!currentInspectionInstance.value) return
  newLabel.value = currentInspectionInstance.value.label || ''
  isEditingLabel.value = true
  nextTick(() => {
    labelInputRef.value?.focus()
  })
}

const cancelEditLabel = () => {
  isEditingLabel.value = false
  newLabel.value = ''
}

const saveLabel = async () => {
  if (!currentInspectionInstance.value?.inspectionInstanceUuid) return

  // Prevent API call if nothing changed
  if (newLabel.value === currentInspectionInstance.value.label) {
    cancelEditLabel()
    return
  }

  try {
    const uuid = currentInspectionInstance.value.inspectionInstanceUuid

    const response = await updateLabel({ uuid, label: newLabel.value })

    if (response.data) {
      currentInspectionInstance.value.label = response.data.label
      ElMessage.success('Label updated successfully')
      isEditingLabel.value = false
    }
  } catch (error) {
    console.error(error)
  }
}

const isMultipleAircraft = computed<boolean>(() => props.aircraftData?.length > 1)

//Filtering by 0 inspection instances will return multiple inspection instances
//as will filtering by multiple inspection instances
//none should currently be possible via page navigation
//but catching to ensure no erreneous visual state and to setup for allowing multiple
const isMultipleInspectionInstances = computed<boolean>(
  () =>
    filterStore.filtersModel.inspectionInstance?.length > 1 || filterStore.filtersModel.inspectionInstance?.length === 0
)

const currentInspectionUuid = computed(() => filterStore.filtersModel.inspectionInstance?.[0])
const currentAircraftUuid = computed(() => filterStore.filtersModel.aircraft?.[0])

const currentInspectionInstance = computed(() =>
  props.inspectionData?.find((inspection) => inspection?.inspectionInstanceUuid === currentInspectionUuid.value)
)
const currentAircraft = computed(() =>
  props.aircraftData?.find((airCraft) => airCraft?.uuid === currentAircraftUuid.value)
)
const currentAircraftInspectionInstances = computed(() =>
  props.inspectionData?.filter((inspectionInstance) => inspectionInstance.aircraftUuid === currentAircraftUuid.value)
)

const severityStats = computed(() => {
  let defaultStats = {
    requires_inspection: 0,
    grounding_condition: 0,
    non_grounding_condition: 0,
    for_awareness: 0,
    suggestion: 0,
    total: 0,
  }
  if (!props.annotationData) return { discrepancies: { ...defaultStats } }
  // set all null values to suggestion as suggestions are not assigned yet
  // and is best to assume null values are suggestions
  const defectStatusCounts = props.annotationData.reduce((acc, item) => {
    const status = item.defectStatus || 'suggestion'
    if (!(status in defaultStats)) return acc
    acc[status] = (acc[status] || 0) + 1
    return acc
  }, defaultStats)

  const total = Object.keys(defectStatusCounts).reduce((acc, item) => {
    return (acc += defectStatusCounts[item])
  }, 0)
  return {
    discrepancies: {
      ...defectStatusCounts,
      total,
    },
  }
})

//TODO need to get a mapping of all URLs for displaying all aircraft - could be a function
//TODO replace firstAircraft and aircraftImageUrl once implementing multiple aircraft summary page view.
</script>

<template>
  <div class="h-full flex flex-col bg-slate-100 border border-gray-200 shadow-lg rounded-lg">
    <!-- TODO need to design/discuss how we want to display multiple aircrafts -->
    <template v-if="!isMultipleAircraft">
      <!-- Avatar and Title Section -->
      <div v-loading="props.aircraftLoading" class="p-6 flex items-center border-b-2 shrink">
        <img :src="currentAircraft?.imageUrl" alt="avatar" class="w-20 h-20 rounded-full" />
        <div class="ml-4 flex flex-col items-start justify-center w-full">
          <h1 class="text-xl font-semibold">{{ currentAircraft?.tailNumber }}</h1>
          <p class="text-gray-500">{{ currentAircraft?.modelName }} | {{ currentAircraft?.owner }}</p>
        </div>
      </div>
    </template>
    <!-- TODO need to design/discuss how we want to display multiple inspection instances -->
    <template v-if="!isMultipleInspectionInstances">
      <!-- Inspection Details Section -->
      <div v-loading="props.inspectionDataLoading" class="p-6 border-b-2 shrink">
        <h3 class="text-lg font-bold text-gray-700 pb-4">Inspection Details</h3>
        <table class="w-full text-left text-gray-500">
          <tbody>
            <tr>
              <td class="py-1 w-24 align-middle">Label:</td>
              <td class="py-1 align-middle">
                <div v-if="!isEditingLabel" class="flex items-center h-8">
                  <span class="mr-2 min-w-[1rem]">{{ currentInspectionInstance?.label || '-' }}</span>

                  <el-icon
                    v-if="currentInspectionInstance"
                    class="cursor-pointer text-gray-400 hover:text-blue-500 transition-colors"
                    @click="startEditingLabel"
                  >
                    <PencilIcon />
                  </el-icon>
                </div>
                <div v-else class="flex items-center gap-1 h-8">
                  <el-input
                    ref="labelInputRef"
                    v-model="newLabel"
                    size="small"
                    maxlength="25"
                    placeholder="Enter label"
                    style="width: 150px"
                    @keyup.enter="saveLabel"
                    @keyup.esc="cancelEditLabel"
                  />
                  <el-button
                    type="primary"
                    circle
                    size="small"
                    :icon="CheckIcon"
                    :loading="isSavingLabel"
                    @click="saveLabel"
                  />
                  <el-button circle size="small" :icon="XMarkIcon" :disabled="isSavingLabel" @click="cancelEditLabel" />
                </div>
              </td>
            </tr>
            <tr>
              <td class="py-1">Date:</td>
              <td class="py-1">
                {{ currentInspectionInstance?.startDate ? formatDate(currentInspectionInstance?.startDate) : '' }}
              </td>
            </tr>
            <tr>
              <td class="py-1">Inspection:</td>
              <td class="py-1">{{ currentInspectionInstance?.type ? currentInspectionInstance.type : '' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
    <!-- Inspection Progress Section -->
    <div v-loading="props.inspectionDataLoading" class="p-6 border-b-2 shrink">
      <h3 class="text-lg font-bold text-gray-700 mb-4">Inspection Progress</h3>
      <div class="mb-6">
        <p class="text-gray-500 mb-1">Images Reviewed</p>
        <el-progress
          :percentage="
            Math.floor(
              (currentInspectionInstance?.imagesReviewedCount / currentInspectionInstance?.imagesCapturedCount) * 100
            ) || 0
          "
          color="#409EFF"
          show-text
          :text-inside="false"
          class="mb-4"
        />
        <!-- <p class="text-sm text-gray-400">{{ reviewedCompletion }}% completed</p> -->
      </div>

      <!-- Progress Bar 2 -->
      <div>
        <p class="text-gray-500 mb-1">Images Captured</p>
        <el-progress
          :percentage="
            Math.floor(
              (currentInspectionInstance?.uniqueImagesCapturedCount / currentInspectionInstance?.missionWaypointCount) *
                100
            ) || 0
          "
          color="#67C23A"
          show-text
          :text-inside="false"
        />
        <!-- <p class="text-sm text-gray-400">{{ capturedCompletion }}% completed</p> -->
      </div>
    </div>
    <div v-loading="props.annotationsLoading" class="p-6 border-b-2 shrink">
      <ReportSummarySeverity :stats="severityStats" />
    </div>
    <!-- AirCraft History Section -->
    <ReportSummaryAircraftHistory
      v-loading="props.inspectionDataLoading"
      :inspection-instances="currentAircraftInspectionInstances"
    />
  </div>
</template>
